import { Injectable, signal } from '@angular/core';

export interface CartAttribute {
  name: string;
  value: string;
}

export interface CartSelectAttribute {
  id: number | string;
  name: string;
  selectedOption: string;
  selectedOptionId: number | string;
}

export interface CartNumberAttribute {
  id: number | string;
  name: string;
  selectedOptionId: number | string;
  selectedOptionValue: string;
}

export interface CartTextAttribute {
  optionId: number | string;
  name: string;
  inputType: string;
  value: string;
}

export interface CartItem {
  cartId: string;
  publicId: string;
  name: string;
  price: number;
  oldPrice: number;
  discountPercentage: number;
  image: string;
  qty: number;
  selectAttributes?: CartAttribute[];
  belowAttributes?: CartAttribute[];
  numberAttributes?: CartAttribute[];
  customization?: string;
  customizationCount?: number;
  // Data for order JSON
  orderSelectAttrs?: CartSelectAttribute[];
  orderNumberAttrs?: CartNumberAttribute[];
  orderTextAttrs?: CartTextAttribute[];
}

const CART_STORAGE_KEY = 'pfc_cart';

@Injectable({ providedIn: 'root' })
export class CartService {

  items = signal<CartItem[]>(this.loadFromStorage());
  isOpen = signal<boolean>(false);

  addItem(item: Omit<CartItem, 'qty' | 'cartId'>, qty = 1): void {
    const current = this.items();
    // Find existing with same product AND same number attributes only
    const existing = current.find(i => {
      if (i.publicId !== item.publicId) return false;
      const iNumbers = JSON.stringify(i.numberAttributes ?? []);
      const newNumbers = JSON.stringify(item.numberAttributes ?? []);
      return iNumbers === newNumbers;
    });

    if (existing) {
      existing.qty = qty;
      if (item.customization) existing.customization = item.customization;
      if (item.customizationCount !== undefined) existing.customizationCount = item.customizationCount;
      if (item.orderSelectAttrs) existing.orderSelectAttrs = item.orderSelectAttrs;
      if (item.orderNumberAttrs) existing.orderNumberAttrs = item.orderNumberAttrs;
      if (item.orderTextAttrs) existing.orderTextAttrs = item.orderTextAttrs;
      this.items.set([...current]);
    } else {
      const cartId = crypto.randomUUID();
      this.items.set([...current, { ...item, qty, cartId }]);
    }
    this.persist();
  }

  updateCustomization(cartId: string, customization: string): void {
    const current = this.items();
    const item = current.find(i => i.cartId === cartId);
    if (item) {
      item.customization = customization;
      item.customizationCount = customization
        ? customization.split(' | ').filter(part => {
            const val = part.split(': ')[1]?.trim();
            return val && val !== 'No' && val !== '';
          }).length
        : 0;
      this.items.set([...current]);
      this.persist();
    }
  }

  increase(cartId: string): void {
    const current = this.items();
    const item = current.find(i => i.cartId === cartId);
    if (item) {
      item.qty++;
      this.items.set([...current]);
      this.persist();
    }
  }

  decrease(cartId: string): void {
    const current = this.items();
    const item = current.find(i => i.cartId === cartId);
    if (item && item.qty > 1) {
      item.qty--;
      this.items.set([...current]);
      this.persist();
    }
  }

  remove(cartId: string): void {
    this.items.set(this.items().filter(i => i.cartId !== cartId));
    this.persist();
  }

  clear(): void {
    this.items.set([]);
    this.persist();
  }

  get total(): number {
    return this.items().reduce((acc, i) => {
      const customCost = (i.customizationCount ?? 0) * 10;
      return acc + (i.price + customCost) * i.qty;
    }, 0);
  }

  open(): void {
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    const items = this.items();
    if (items.length > 0) {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    } else {
      localStorage.removeItem(CART_STORAGE_KEY);
    }
  }

  private loadFromStorage(): CartItem[] {
    if (typeof localStorage === 'undefined') return [];
    try {
      const raw = localStorage.getItem(CART_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter(item => this.isValidCartItem(item));
    } catch {
      localStorage.removeItem(CART_STORAGE_KEY);
      return [];
    }
  }

  private isValidCartItem(item: any): item is CartItem {
    return (
      item &&
      typeof item === 'object' &&
      typeof item.cartId === 'string' &&
      typeof item.publicId === 'string' &&
      typeof item.name === 'string' &&
      typeof item.price === 'number' &&
      typeof item.oldPrice === 'number' &&
      typeof item.discountPercentage === 'number' &&
      typeof item.image === 'string' &&
      typeof item.qty === 'number' &&
      item.qty > 0
    );
  }
}
